import { handleActions } from 'redux-actions';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

import createCalendar, { getDateRangeFromCalendar } from '../shared/calendar';
import createFilters, { getFiltersObject } from '../shared/filters';

import { createTableWithSorting } from '../shared/new-table';

import endpoints from '../../config/endpoints';

import { regions } from '../../../../../enums/regions';

import tableInitialData from './table-initial-data';
import createList from './create-list';

import reasons from './reasons';

export const namespace = 'alertsAnalytics';

const table = createTableWithSorting({
	namespace,
	initialState: {
		table: tableInitialData.table,
		sortedBy: tableInitialData.sortedBy
	},
	sortingConfig: {
		sort
	}
});

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'alerts',
			title: 'Alerts',
			placeholder: 'Add alerts',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ALERT_EVENTS
			}
		},
		{
			name: 'managers',
			title: 'Managers',
			placeholder: 'Add managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		},
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'countries',
			title: 'Countries',
			placeholder: 'Add countries',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'reasons',
			title: 'Reasons',
			placeholder: 'Add reasons',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: reasons
			}
		}
	]
});

const list = createList({
	namespace,
	calendar,
	table,
	filters,
	url: 'v2/traffic-alerts',
	perPage: 50,
	getRequestQuery
});

const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;

// initial state
const initialState = {
	...list.initialState,
	table: table.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState
};

// actions
export const actions = {
	apply: () => (dispatch, getState) => {
		dispatch({ type: APPLY });
		dispatch(list.actions.own.fetchData());
	},
	clear: () => (dispatch, getState) => {
		dispatch({ type: CLEAR });
		dispatch(list.actions.own.fetchData());
	},
	...list.actions,
	table: table.actions,
	filters: filters.actions,
	calendar: calendar.actions
};

const reducers = Object.assign(list.reducers, table.reducers, filters.reducers, calendar.reducers, {
	[APPLY]: (state, action) => ({
		...state,
		data: null,
		isLoading: true
	}),
	[CLEAR]: (state, action) => initialState
});

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	...list.selectors,
	getTable: table.selectors.getTable,
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state)
};

function getRequestQuery(page, getState) {
	const state = getState()[namespace];

	const sorting = state.table.sortedBy.column;

	const limit = state.pagination.perPage;
	const range = getDateRangeFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		offset: (page - 1) * limit,
		limit,
		dates: [range.start, range.end],
		sorting: {
			field: sorting.index,
			direction: sorting.direction
		},
		filters,
		inverse_filters
	};
}

function sort(remote) {
	return (dispatch, getState) => {
		if (remote) {
			dispatch(actions.own.fetchData());
		} else {
			const state = getState()[namespace];

			const column = state.table.sortedBy.column;

			dispatch({
				type: `${namespace}/fetch-data-success`,
				payload: {
					data: {
						rows: (column.index === 'leads' ? sortByNumber : orderBy)(state.data, column.index, column.direction)
					}
				}
			});
		}
	};
}
function sortByNumber(list, index, direction) {
	return list.sort((a, b) => {
		if (a[index] === b[index]) return 0;

		if (a[index] === null) return +1;
		if (b[index] === null) return -1;

		return direction === 'asc'
			? Number(a[index]) - Number(b[index])
			: Number(b[index]) - Number(a[index]);
	});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/alerts-analytics/index.js